import { useState, useCallback, useMemo, useEffect } from 'react';
import { ButtonBase, MenuItem, Typography, Stack } from '@mui/material';
import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { useSituation } from 'src/services/situation/useSituation';
import { useData } from 'src/services/data/useData';
import moment from 'moment';

export default function ReportingGlobalYearSelector() {
  const {
    situationPeriods,
    marketDemands,
    selectedMarket,
    getUpdatedSituationData,
    marketReferenceContractOverview,
    reportingUserPreferences,
    marketReferenceContract,
    marketContracts,
    updateReferenceContract,
    selectedSituationDate,
  } = useSituation();

  const { setMultiData } = useData();

  const [prevSelectedSituationDate, setPrevSelectedSituationDate] = useState(
    selectedSituationDate
  );
  const [prevSelectedMarket, setPrevSelectedMarket] = useState(selectedMarket);

  const popover = usePopover();

  // Note: Commodity are hardcoded since the reference contract transaction are
  // only applicable to Gas and Elec contract
  const marketCommodityType =
    marketReferenceContract?.underlying_commodity === 2
      ? 'gas'
      : marketReferenceContract?.underlying_commodity === 3
        ? 'electricity'
        : '';
  // Remove dateRange from the available periods
  const periods = useMemo(() => [...situationPeriods], [situationPeriods]);
  const indexDateRange = periods.findIndex(
    (period) => period.id === 'dateRange'
  );
  if (indexDateRange !== -1) periods.splice(indexDateRange, 1);

  const initialTabIndex = periods.indexOf(
    situationPeriods.find((p) => p.id === 'Y')
  );

  const defaultPeriod = periods[initialTabIndex];
  const [selectedPeriod, setSelectedPeriod] = useState(defaultPeriod);

  const alignReferenceContract = useCallback(
    async (period) => {
      const asContractPreference = reportingUserPreferences.find(
        (pref) => pref.market === selectedMarket.id
      );

      const availableContracts = asContractPreference
        ? asContractPreference.accessible_contracts
        : marketContracts;

      let referenceContract = null;

      // Use the Calendar contract as reference for the corresponding year
      if (period.id === 'Y+1' || period.id === 'Y+2' || period.id === 'Y+3') {
        const yearMatch = period.id.match(/^Y\+(\d)$/);
        const year = yearMatch ? yearMatch[1] : null;
        const baseName =
          marketCommodityType === 'electricity' ? 'Base Calendar' : 'Calendar';
        referenceContract = availableContracts.find((contract) =>
          contract.name.includes(`${baseName} ${year}`)
        );
      } else {
        referenceContract = asContractPreference
          ? asContractPreference.reference_contract
          : marketContracts[0];
      }

      if (referenceContract) {
        // Only update the marketReferenceContract in the reducer
        await updateReferenceContract(
          referenceContract,
          asContractPreference ? true : false
        );
      }
    },
    [
      marketCommodityType,
      marketContracts,
      reportingUserPreferences,
      selectedMarket.id,
      updateReferenceContract,
    ]
  );

  const handleGlobalChangePeriod = useCallback(
    (period) => {
      popover.onClose();
      setSelectedPeriod(period);

      // Timestamp allow the refresh when reselecting the same year
      const periodWithTimestamp = { ...period, timestamp: Date.now() };

      // update global year selected in reducer
      setMultiData({
        selectedGlobalSituationYear: periodWithTimestamp,
        marketDemandSelectedPeriod: period,
      });

      if (
        marketCommodityType === 'gas' ||
        marketCommodityType === 'electricity'
      ) {
        alignReferenceContract(period);
      }

      const { lastPrice } = marketReferenceContractOverview;
      // Fetch updated data for Summary dashboard
      const startDate = moment(period.label)
        .startOf('year')
        .format('YYYY-MM-DD');
      const endDate = moment(period.label).endOf('year').format('YYYY-MM-DD');
      const targetPrice =
        marketDemands[0]?.situationTargetPrice?.history.find(
          (elem) => moment(elem.start_date).year() === Number(period.label)
        )?.value ||
        lastPrice ||
        0;

      getUpdatedSituationData(startDate, endDate, targetPrice);
    },
    [
      alignReferenceContract,
      getUpdatedSituationData,
      marketCommodityType,
      marketDemands,
      marketReferenceContractOverview,
      popover,
      setMultiData,
    ]
  );

  useEffect(() => {
    if (
      (selectedSituationDate &&
        prevSelectedSituationDate !== selectedSituationDate) ||
      (selectedMarket && prevSelectedMarket !== selectedMarket && defaultPeriod)
    ) {
      setPrevSelectedSituationDate(selectedSituationDate);
      setPrevSelectedMarket(selectedMarket);
      setSelectedPeriod(defaultPeriod);
    }
  }, [
    defaultPeriod,
    prevSelectedMarket,
    prevSelectedSituationDate,
    selectedMarket,
    selectedSituationDate,
  ]);

  return (
    <Stack
      component="nav"
      direction="row"
      alignItems="center"
      spacing={5}
      sx={{ mr: 2.5, height: 1 }}
    >
      <ButtonBase
        onClick={popover.onOpen}
        sx={{
          pl: 2,
          py: 1.5,
          pr: 1,
          mt: 0,
          maxHeight: 0.7,
          borderRadius: 1,
          boxShadow:
            '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
          typography: 'subtitle1',
          border: '1px solid rgba(145, 158, 171, 0.32)',
          bgcolor: '#FFFFFF',
          textAlign: 'left',
          pointerEvents: 'all',
          gap: '8px',
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Reporting Year
        </Typography>
        <Typography
          // variant="h6"
          sx={{ fontSize: '1rem', fontWeight: 'bold', mt: 0.25 }}
        >
          {selectedPeriod?.label || defaultPeriod?.label}
        </Typography>

        <Iconify
          width={16}
          icon={
            popover.open
              ? 'eva:arrow-ios-upward-fill'
              : 'eva:arrow-ios-downward-fill'
          }
          sx={{ ml: 1 }}
        />
      </ButtonBase>
      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        sx={{ width: 140 }}
      >
        {periods.map((period) => (
          <MenuItem
            key={period.id}
            selected={period?.label === selectedPeriod?.label}
            onClick={() => handleGlobalChangePeriod(period)}
          >
            {period?.label}
          </MenuItem>
        ))}
      </CustomPopover>
    </Stack>
  );
}
